import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, TextField, Button, Tooltip, Alert, Chip, List, ListItemText, ListItemIcon, Divider, Avatar, Badge, Paper, Switch, FormControlLabel, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Notifications as NotificationIcon,
  Check as MarkReadIcon,
  CheckBoxOutlineBlank as MarkUnreadIcon,
  Send as SendIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { SectionToolbar } from '@shared/SectionToolbar';
import StatusChip from '@shared/StatusChip';
import EmptyState from '@shared/EmptyState';
import { TableSkeleton } from '@shared/TableSkeleton';
import KPICard from '@shared/KPICard';
import { api } from '@services/authService';
import { formatForDisplay, formatDateOnly, formatTimeOnly } from '@utils/dateFormatter';

interface Notification {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success' | 'system';
  priority: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message: string;
  recipientId?: string;
  recipientName?: string;
  recipientType?: 'user' | 'clinic' | 'system';
  senderId?: string;
  senderName?: string;
  status: 'sent' | 'delivered' | 'read' | 'failed';
  read: boolean;
  readAt?: string;
  sentAt: string;
  data?: any;
  channels: ('email' | 'sms' | 'push' | 'in_app')[];
  category: 'system' | 'billing' | 'security' | 'maintenance' | 'promotion' | 'reminder';
}

interface NotificationStats {
  totalNotifications: number;
  unreadNotifications: number;
  sentToday: number;
  failedNotifications: number;
  byType: {
    info: number;
    warning: number;
    error: number;
    success: number;
    system: number;
  };
  byChannel: {
    email: number;
    sms: number;
    push: number;
    in_app: number;
  };
}

export default function NotificationsManagement() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [composeDialogOpen, setComposeDialogOpen] = useState(false);
  const [composeForm, setComposeForm] = useState({
    type: 'info',
    priority: 'medium',
    title: '',
    message: '',
    recipientType: 'all',
    recipientId: '',
    channels: ['in_app'],
    category: 'system'
  });

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/master-admin/notifications', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm,
          type: typeFilter,
          status: statusFilter,
          priority: priorityFilter
        }
      });

      setNotifications(response.data.data?.notifications || []);
      setTotal(response.data.data?.pagination?.total || 0);
    } catch (err: any) {
      console.error('Error loading notifications:', err);
      setError('Eroare la incarcarea notificarilor');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/master-admin/notifications/stats');
      setStats(response.data.data);
    } catch (err: any) {
      console.error('Error loading notification stats:', err);
    }
  };

  useEffect(() => {
    loadNotifications();
    loadStats();
  }, [page, rowsPerPage, searchTerm, typeFilter, statusFilter, priorityFilter]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };

  const handleFilterChange = (filters: string[]) => {
    setTypeFilter(filters);
    setPage(0);
  };

  const handleStatusFilterChange = (filters: string[]) => {
    setStatusFilter(filters);
    setPage(0);
  };

  const handlePriorityFilterChange = (filters: string[]) => {
    setPriorityFilter(filters);
    setPage(0);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, notification: Notification) => {
    setAnchorEl(event.currentTarget);
    setSelectedNotification(notification);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedNotification(null);
  };

  const handleViewNotification = () => {
    setViewDialogOpen(true);
    setAnchorEl(null);
  };

  const handleMarkAsRead = async () => {
    if (selectedNotification) {
      try {
        await api.patch(`/master-admin/notifications/${selectedNotification.id}/read`);
        await loadNotifications();
        await loadStats();
      } catch (err) {
        console.error('Error marking notification as read:', err);
      }
    }
    setAnchorEl(null);
  };

  const handleMarkAsUnread = async () => {
    if (selectedNotification) {
      try {
        await api.patch(`/master-admin/notifications/${selectedNotification.id}/unread`);
        await loadNotifications();
        await loadStats();
      } catch (err) {
        console.error('Error marking notification as unread:', err);
      }
    }
    setAnchorEl(null);
  };

  const handleDeleteNotification = async () => {
    if (selectedNotification) {
      try {
        await api.delete(`/master-admin/notifications/${selectedNotification.id}`);
        await loadNotifications();
        await loadStats();
      } catch (err) {
        console.error('Error deleting notification:', err);
      }
    }
    setAnchorEl(null);
  };

  const handleSendNotification = async () => {
    try {
      await api.post('/master-admin/notifications', composeForm);
      await loadNotifications();
      await loadStats();
      setComposeDialogOpen(false);
      setComposeForm({
        type: 'info',
        priority: 'medium',
        title: '',
        message: '',
        recipientType: 'all',
        recipientId: '',
        channels: ['in_app'],
        category: 'system'
      });
    } catch (err) {
      console.error('Error sending notification:', err);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/master-admin/notifications/export', {
        params: {
          search: searchTerm,
          type: typeFilter,
          status: statusFilter,
          priority: priorityFilter
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `notificari-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting notifications:', err);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'info': return <InfoIcon />;
      case 'warning': return <WarningIcon />;
      case 'error': return <ErrorIcon />;
      case 'success': return <SuccessIcon />;
      case 'system': return <NotificationIcon />;
      default: return <NotificationIcon />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'info': return 'info';
      case 'warning': return 'warning';
      case 'error': return 'error';
      case 'success': return 'success';
      case 'system': return 'primary';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'default';
      case 'medium': return 'primary';
      case 'high': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getRecipientIcon = (type?: string) => {
    switch (type) {
      case 'clinic': return <BusinessIcon />;
      case 'user': return <PersonIcon />;
      default: return <NotificationIcon />;
    }
  };

  const typeFilterOptions = [
    { key: 'info', label: 'Informatii', value: 'info' },
    { key: 'warning', label: 'Avertismente', value: 'warning' },
    { key: 'error', label: 'Erori', value: 'error' },
    { key: 'success', label: 'Succes', value: 'success' },
    { key: 'system', label: 'Sistem', value: 'system' }
  ];

  const statusFilterOptions = [
    { key: 'sent', label: 'Trimise', value: 'sent' },
    { key: 'delivered', label: 'Livrate', value: 'delivered' },
    { key: 'read', label: 'Citite', value: 'read' },
    { key: 'failed', label: 'Esuate', value: 'failed' }
  ];

  const priorityFilterOptions = [
    { key: 'low', label: 'Scazuta', value: 'low' },
    { key: 'medium', label: 'Medie', value: 'medium' },
    { key: 'high', label: 'Ridicata', value: 'high' },
    { key: 'critical', label: 'Critica', value: 'critical' }
  ];

  if (error) {
    return (
      <EmptyState
        icon={<NotificationIcon />}
        title="Eroare la incarcare"
        description="Nu am putut incarca lista de notificari. Verifica conexiunea la internet si incearca din nou."
        action={{
          label: "Incearca din nou",
          onClick: loadNotifications
        }}
      />
    );
  }

  return (
    <Box>
      <SectionToolbar
        title="Gestionare Notificari"
        searchValue={searchTerm}
        onSearchChange={handleSearchChange}
        onRefresh={loadNotifications}
        onAdd={() => setComposeDialogOpen(true)}
        filters={typeFilterOptions}
        activeFilters={typeFilter}
        onFilterChange={handleFilterChange}
        loading={loading}
        onExport={handleExport}
      />

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KPICard
            icon={<NotificationIcon />}
            title="Total Notificari"
            value={stats?.totalNotifications || 0}
            subtitle={`${stats?.unreadNotifications || 0} necitite`}
            color="primary"
            onClick={() => window.location.href = '/master-admin/notifications'}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KPICard
            icon={<SendIcon />}
            title="Trimise Astazi"
            value={stats?.sentToday || 0}
            subtitle="Notificari trimise"
            color="success"
            onClick={() => window.location.href = '/master-admin/notifications?period=today'}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KPICard
            icon={<ErrorIcon />}
            title="Esuate"
            value={stats?.failedNotifications || 0}
            subtitle="Notificari esuate"
            color="error"
            onClick={() => window.location.href = '/master-admin/notifications?status=failed'}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KPICard
            icon={<InfoIcon />}
            title="Rate Livrare"
            value={`${stats ? Math.round(((stats.totalNotifications - stats.failedNotifications) / stats.totalNotifications) * 100) : 0}%`}
            subtitle="Notificari livrate"
            color="info"
            onClick={() => window.location.href = '/master-admin/notifications?status=delivered'}
          />
        </Grid>
      </Grid>

      {loading ? (
        <TableSkeleton rows={10} columns={7} />
      ) : notifications.length === 0 ? (
        <EmptyState
          icon={<NotificationIcon />}
          title="Nicio notificare gasita"
          description="Nu exista notificari care sa corespunda criteriilor de cautare."
          action={{
            label: "Trimite notificare",
            onClick: () => setComposeDialogOpen(true)
          }}
        />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tip</TableCell>
                <TableCell>Titlu</TableCell>
                <TableCell>Destinatar</TableCell>
                <TableCell>Prioritate</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Canale</TableCell>
                <TableCell>Trimisa</TableCell>
                <TableCell align="right">Actiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notifications.map((notification) => (
                <TableRow key={notification.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ 
                        width: 32, 
                        height: 32, 
                        bgcolor: `${getTypeColor(notification.type)}.light`,
                        color: `${getTypeColor(notification.type)}.contrastText`
                      }}>
                        {getTypeIcon(notification.type)}
                      </Avatar>
                      <Chip 
                        label={notification.type.toUpperCase()}
                        size="small"
                        color={getTypeColor(notification.type)}
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {notification.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {notification.message.substring(0, 50)}...
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getRecipientIcon(notification.recipientType)}
                      <Box>
                        <Typography variant="body2">
                          {notification.recipientName || 'Toate clinicile'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {notification.recipientType || 'sistem'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={notification.priority.toUpperCase()}
                      size="small"
                      color={getPriorityColor(notification.priority)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={notification.status.toUpperCase()}
                        size="small"
                        color={notification.status === 'read' ? 'success' : 
                               notification.status === 'delivered' ? 'info' :
                               notification.status === 'sent' ? 'warning' : 'error'}
                        variant="outlined"
                      />
                      {!notification.read && (
                        <Badge color="primary" variant="dot" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {notification.channels && notification.channels.map((channel, index) => (
                        <Chip 
                          key={index}
                          label={channel}
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {formatDateOnly(notification.sentAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTimeOnly(notification.sentAt)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, notification)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Randuri pe pagina:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} din ${count !== -1 ? count : `mai mult de ${to}`}`
            }
          />
        </TableContainer>
      )}

      {/* View Notification Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalii Notificare</DialogTitle>
        <DialogContent>
          {selectedNotification && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid size={12}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ 
                        width: 48, 
                        height: 48, 
                        bgcolor: `${getTypeColor(selectedNotification.type)}.light`,
                        color: `${getTypeColor(selectedNotification.type)}.contrastText`
                      }}>
                        {getTypeIcon(selectedNotification.type)}
                      </Avatar>
                      <Box>
                        <Typography variant="h6">{selectedNotification.title}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Chip 
                            label={selectedNotification.type.toUpperCase()}
                            size="small"
                            color={getTypeColor(selectedNotification.type)}
                          />
                          <Chip 
                            label={selectedNotification.priority.toUpperCase()}
                            size="small"
                            color={getPriorityColor(selectedNotification.priority)}
                          />
                          <Chip 
                            label={selectedNotification.status.toUpperCase()}
                            size="small"
                            color={selectedNotification.status === 'read' ? 'success' : 
                                   selectedNotification.status === 'delivered' ? 'info' :
                                   selectedNotification.status === 'sent' ? 'warning' : 'error'}
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </Box>
                    
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedNotification.message}
                    </Typography>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Destinatar
                        </Typography>
                        <Typography variant="body2">
                          {selectedNotification.recipientName || 'Toate clinicile'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Trimisa la
                        </Typography>
                        <Typography variant="body2">
                          {formatForDisplay(selectedNotification.sentAt)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Canale
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {selectedNotification.channels && selectedNotification.channels.map((channel, index) => (
                            <Chip 
                              key={index}
                              label={channel}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Categorie
                        </Typography>
                        <Typography variant="body2">
                          {selectedNotification.category}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Inchide
          </Button>
        </DialogActions>
      </Dialog>

      {/* Compose Notification Dialog */}
      <Dialog open={composeDialogOpen} onClose={() => setComposeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Trimite Notificare</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Tip</InputLabel>
                <Select
                  value={composeForm.type}
                  onChange={(e) => setComposeForm({...composeForm, type: e.target.value})}
                  label="Tip"
                >
                  {typeFilterOptions.map((type) => (
                    <MenuItem key={type.key} value={type.key}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Prioritate</InputLabel>
                <Select
                  value={composeForm.priority}
                  onChange={(e) => setComposeForm({...composeForm, priority: e.target.value})}
                  label="Prioritate"
                >
                  {priorityFilterOptions.map((priority) => (
                    <MenuItem key={priority.key} value={priority.key}>
                      {priority.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Titlu"
                value={composeForm.title}
                onChange={(e) => setComposeForm({...composeForm, title: e.target.value})}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Mesaj"
                multiline
                rows={4}
                value={composeForm.message}
                onChange={(e) => setComposeForm({...composeForm, message: e.target.value})}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Destinatar</InputLabel>
                <Select
                  value={composeForm.recipientType}
                  onChange={(e) => setComposeForm({...composeForm, recipientType: e.target.value})}
                  label="Destinatar"
                >
                  <MenuItem value="all">Toate clinicile</MenuItem>
                  <MenuItem value="clinic">Clinica specifica</MenuItem>
                  <MenuItem value="user">Utilizator specific</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="ID Destinatar"
                value={composeForm.recipientId}
                onChange={(e) => setComposeForm({...composeForm, recipientId: e.target.value})}
                disabled={composeForm.recipientType === 'all'}
              />
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Canale de trimitere
              </Typography>
              <Stack direction="row" spacing={1}>
                {['email', 'sms', 'push', 'in_app'].map((channel) => (
                  <FormControlLabel
                    key={channel}
                    control={
                      <Switch
                        checked={composeForm.channels.includes(channel as any)}
                        onChange={(e) => {
                          const channels = e.target.checked
                            ? [...composeForm.channels, channel]
                            : composeForm.channels.filter(c => c !== channel);
                          setComposeForm({...composeForm, channels});
                        }}
                      />
                    }
                    label={channel}
                  />
                ))}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComposeDialogOpen(false)}>
            Anuleaza
          </Button>
          <Button onClick={handleSendNotification} variant="contained">
            Trimite
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewNotification}>
          <VisibilityIcon sx={{ mr: 1 }} />
          Vezi detalii
        </MenuItem>
        {selectedNotification?.read ? (
          <MenuItem onClick={handleMarkAsUnread}>
            <MarkUnreadIcon sx={{ mr: 1 }} />
            Marcheaza ca necitita
          </MenuItem>
        ) : (
          <MenuItem onClick={handleMarkAsRead}>
            <MarkReadIcon sx={{ mr: 1 }} />
            Marcheaza ca citita
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteNotification} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Sterge
        </MenuItem>
      </Menu>
    </Box>
  );
}
